import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import MobileTable from '../common/MobileTable';
import CustomDropdown from '../common/CustomDropdown';
import DeleteConfirmModal from '../common/DeleteConfirmModal';
import { exportCreditHistoryToPDF } from '../../utils/pdfExport';

const CreditHoldersTab = () => {
  const [creditHolders, setCreditHolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [showCreditHolderHistory, setShowCreditHolderHistory] = useState(false);
  const [selectedCreditHolder, setSelectedCreditHolder] = useState(null);
  const [creditHolderHistory, setCreditHolderHistory] = useState([]);
  const [editingCreditHolder, setEditingCreditHolder] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    amountPayable: ''
  });
  const [collectData, setCollectData] = useState({
    creditHolderId: '',
    amountCollected: '',
    collectedIn: 'cash_balance'
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchCreditHolders();
  }, []);

  const fetchCreditHolders = async () => {
    try {
      const response = await api.get('/credit-holders');
      setCreditHolders(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch credit holders');
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditHolderHistory = async (creditHolderId) => {
    try {
      const response = await api.get(`/credit-holders/${creditHolderId}/history`);
      setCreditHolderHistory(response.data);
    } catch (err) {
      console.error('Failed to fetch credit holder history:', err);
      setError('Failed to fetch credit history');
    }
  };

  const handleShowCreditHolderHistory = async (creditHolder) => {
    setSelectedCreditHolder(creditHolder);
    await fetchCreditHolderHistory(creditHolder.id);
    setShowCreditHolderHistory(true);
  };

  const handleCloseCreditHolderHistory = () => {
    setShowCreditHolderHistory(false);
    setSelectedCreditHolder(null);
    setCreditHolderHistory([]);
  };

  const handleOpenModal = (creditHolder = null) => {
    if (creditHolder) {
      setEditingCreditHolder(creditHolder);
      setFormData({
        name: creditHolder.name,
        address: creditHolder.address || '',
        phone: creditHolder.phone || '',
        amountPayable: creditHolder.amount_payable || ''
      });
    } else {
      setEditingCreditHolder(null);
      setFormData({
        name: '',
        address: '',
        phone: '',
        amountPayable: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCreditHolder(null);
    setFormData({
      name: '',
      address: '',
      phone: '',
      amountPayable: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingCreditHolder) {
        await api.put(`/credit-holders/${editingCreditHolder.id}`, formData);
      } else {
        await api.post('/credit-holders', formData);
      }
      await fetchCreditHolders();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Operation failed');
    }
  };

  const handleOpenCollectModal = () => {
    setCollectData({
      creditHolderId: '',
      amountCollected: '',
      collectedIn: 'cash_balance'
    });
    setShowCollectModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseCollectModal = () => {
    setShowCollectModal(false);
    setCollectData({
      creditHolderId: '',
      amountCollected: '',
      collectedIn: 'cash_balance'
    });
  };

  const handleCollectCredit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!collectData.creditHolderId || !collectData.amountCollected || !collectData.collectedIn) {
      setError('Please select a credit holder, enter amount, and select collection account');
      return;
    }

    const amountCollected = parseFloat(collectData.amountCollected);
    if (amountCollected <= 0) {
      setError('Amount collected must be greater than 0');
      return;
    }

    const selectedHolder = creditHolders.find(ch => ch.id === collectData.creditHolderId);
    if (!selectedHolder) {
      setError('Credit holder not found');
      return;
    }

    const currentPayable = parseFloat(selectedHolder.amount_payable || 0);
    if (amountCollected > currentPayable) {
      setError(`Amount collected (₹${amountCollected.toFixed(2)}) cannot exceed outstanding payable (₹${currentPayable.toFixed(2)})`);
      return;
    }

    try {
      await api.post('/credit-holders/collect', {
        creditHolderId: collectData.creditHolderId,
        amountCollected: amountCollected,
        collectedIn: collectData.collectedIn
      });

      setSuccess(`Successfully collected ₹${amountCollected.toFixed(2)} from ${selectedHolder.name}`);
      await fetchCreditHolders();
      handleCloseCollectModal();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to collect credit');
    }
  };

  const handleDeleteCollection = (collection) => {
    setDeleteTarget(collection);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCollection = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    setError('');

    try {
      await api.delete(`/credit-holders/collection/${deleteTarget.id}`);
      setSuccess(`Credit collection of ₹${parseFloat(deleteTarget.amount_collected).toFixed(2)} deleted successfully`);

      // Refresh data
      await Promise.all([
        fetchCreditHolders(),
        selectedCreditHolder ? fetchCreditHolderHistory(selectedCreditHolder.id) : Promise.resolve()
      ]);

      setShowDeleteConfirm(false);
      setDeleteTarget(null);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete collection');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportPDF = () => {
    if (selectedCreditHolder && creditHolderHistory) {
      exportCreditHistoryToPDF(selectedCreditHolder, creditHolderHistory);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'address', label: 'Address', render: (ch) => ch.address || '-' },
    { key: 'phone', label: 'Phone', render: (ch) => ch.phone || '-' },
    {
      key: 'amount_payable',
      label: 'Amount Payable',
      render: (ch) => (
        <span style={{ fontWeight: '700', color: '#ff9800', fontSize: '1.125rem' }}>
          ₹{parseFloat(ch.amount_payable || 0).toFixed(2)}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (ch) => new Date(ch.created_at).toLocaleDateString()
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (ch) => (
        <div className="action-buttons">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleShowCreditHolderHistory(ch);
            }}
            className="btn btn-primary"
          >
            History
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenModal(ch);
            }}
            className="btn btn-secondary"
          >
            Edit
          </button>
        </div>
      )
    }
  ];

  return (
    <>
      {success && <div className="success" style={{ marginBottom: '1rem' }}>{success}</div>}
      {error && !showModal && !showCollectModal && (
        <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>
      )}

      <div className="mobile-stack" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem' }}>
        <h2 style={{ color: '#000', margin: 0, fontSize: 'clamp(1.5rem, 5vw, 2rem)', fontWeight: '700', letterSpacing: '0.5px' }}>
          Credit Holders
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={handleOpenCollectModal}
            className="btn btn-primary"
            style={{ background: '#4CAF50', borderColor: '#000' }}
          >
            Collect Credit
          </button>
          <button onClick={() => handleOpenModal()} className="btn btn-success">
            Add New
          </button>
        </div>
      </div>

      <MobileTable
        columns={columns}
        data={creditHolders}
        onRowClick={handleShowCreditHolderHistory}
        enableSearch={true}
        enableSort={true}
        defaultSortKey="name"
        defaultSortOrder="asc"
      />


      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingCreditHolder ? 'Edit Credit Holder' : 'Add New Credit Holder'}</h2>
            {error && <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <textarea
                  id="address"
                  className="form-control"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  className="form-control"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              {!editingCreditHolder && (
                <div className="form-group">
                  <label htmlFor="amountPayable">Initial Outstanding Amount (Optional)</label>
                  <input
                    type="number"
                    id="amountPayable"
                    className="form-control"
                    value={formData.amountPayable}
                    onChange={(e) => setFormData({ ...formData, amountPayable: e.target.value })}
                    step="0.01"
                    min="0"
                    placeholder="₹ 0.00"
                  />
                  <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.5rem', display: 'block' }}>
                    Set the initial outstanding amount if this credit holder already owes money
                  </small>
                </div>
              )}

              {editingCreditHolder && (
                <div className="form-group">
                  <label htmlFor="amountPayable">Amount Payable (Read-Only)</label>
                  <input
                    type="number"
                    id="amountPayable"
                    className="form-control"
                    value={formData.amountPayable}
                    readOnly
                    style={{
                      backgroundColor: '#e9ecef',
                      cursor: 'not-allowed',
                      fontWeight: '600',
                      color: '#000'
                    }}
                  />
                  <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.5rem', display: 'block' }}>
                    Amount payable is automatically calculated from credits given and collections
                  </small>
                </div>
              )}

              <div className="modal-buttons">
                <button type="submit" className="btn btn-primary">
                  {editingCreditHolder ? 'Update' : 'Create'}
                </button>
                <button type="button" onClick={handleCloseModal} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCollectModal && (
        <div className="modal-overlay" onClick={handleCloseCollectModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Collect Credit</h2>
            </div>

            {error && <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>}
            {success && <div className="success" style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#d4edda', color: '#155724', borderRadius: '8px', border: '1px solid #c3e6cb' }}>{success}</div>}

            <form onSubmit={handleCollectCredit}>
              <div className="form-group">
                <CustomDropdown
                  label="Select Credit Holder"
                  value={collectData.creditHolderId}
                  onChange={(value) => setCollectData({ ...collectData, creditHolderId: value })}
                  options={[
                    { value: '', label: '-- Select Credit Holder --' },
                    ...creditHolders
                      .filter(ch => parseFloat(ch.amount_payable || 0) > 0)
                      .map(ch => ({
                        value: ch.id,
                        label: `${ch.name} - Outstanding: ₹${parseFloat(ch.amount_payable || 0).toFixed(2)}`
                      }))
                  ]}
                  placeholder="-- Select Credit Holder --"
                />
              </div>

              {collectData.creditHolderId && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  border: '1px solid #dee2e6'
                }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>Outstanding Payable</p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.5rem', fontWeight: '700', color: '#000' }}>
                    ₹{parseFloat(creditHolders.find(ch => ch.id === collectData.creditHolderId)?.amount_payable || 0).toFixed(2)}
                  </p>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="amountCollected">Amount Collected</label>
                <input
                  type="number"
                  id="amountCollected"
                  className="form-control"
                  value={collectData.amountCollected}
                  onChange={(e) => setCollectData({ ...collectData, amountCollected: e.target.value })}
                  step="0.01"
                  min="0"
                  required
                  placeholder="Enter amount collected"
                  style={{ padding: '0.75rem', fontSize: '1rem' }}
                />
              </div>

              <div className="form-group">
                <CustomDropdown
                  label="Collected In | में एकत्रित"
                  value={collectData.collectedIn}
                  onChange={(value) => setCollectData({ ...collectData, collectedIn: value })}
                  options={[
                    { value: 'cash_balance', label: 'Cash Balance | नकद शेष' },
                    { value: 'bank_balance', label: 'Bank Balance | बैंक शेष' },
                    { value: 'gala_balance', label: 'Gala Balance | गला शेष' }
                  ]}
                  placeholder="Select balance account"
                />
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem',
                  backgroundColor: '#e8f5e9',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  color: '#2e7d32'
                }}>
                  💡 Select which balance account to add this collection to
                </div>
              </div>

              {collectData.creditHolderId && collectData.amountCollected && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#e8f5e9',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  border: '1px solid #4CAF50'
                }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#2e7d32' }}>New Outstanding After Collection</p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.5rem', fontWeight: '700', color: '#1b5e20' }}>
                    ₹{(parseFloat(creditHolders.find(ch => ch.id === collectData.creditHolderId)?.amount_payable || 0) - parseFloat(collectData.amountCollected || 0)).toFixed(2)}
                  </p>
                </div>
              )}

              <div className="modal-buttons">
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{
                    background: '#4CAF50',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    borderColor: '#000'
                  }}
                >
                  Collect Credit
                </button>
                <button type="button" onClick={handleCloseCollectModal} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreditHolderHistory && selectedCreditHolder && (
        <div className="modal-overlay" onClick={handleCloseCreditHolderHistory}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <h2 style={{ margin: 0 }}>Credit History - {selectedCreditHolder.name}</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handleExportPDF}
                  className="btn btn-primary"
                  style={{ padding: '0.5rem 1rem', background: '#2196F3', borderColor: '#000' }}
                >
                  Export PDF
                </button>
                <button
                  onClick={handleCloseCreditHolderHistory}
                  className="btn btn-secondary"
                  style={{ padding: '0.5rem 1rem' }}
                >
                  Close
                </button>
              </div>
            </div>

            {error && <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>}

            <div style={{
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>Contact</p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '1rem', fontWeight: '600' }}>
                    {selectedCreditHolder.phone || 'N/A'}
                  </p>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>Current Outstanding</p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.25rem', fontWeight: '700', color: '#dc3545' }}>
                    ₹{parseFloat(selectedCreditHolder.amount_payable || 0).toFixed(2)}
                  </p>
                </div>
              </div>
              {selectedCreditHolder.address && (
                <div style={{ marginTop: '0.75rem' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>Address</p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '1rem' }}>
                    {selectedCreditHolder.address}
                  </p>
                </div>
              )}
            </div>

            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
              Transaction History ({creditHolderHistory.length} records)
            </h3>

            {creditHolderHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                No transaction history found for this credit holder
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="table">
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                    <tr>
                      <th>Date & Time</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Collected In</th>
                      <th>Previous Balance</th>
                      <th>New Balance</th>
                      <th>User</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditHolderHistory.map(record => {
                      const transactionType = record.transaction_type || 'collected';
                      const isGiven = transactionType === 'given';
                      const collectedInDisplay = record.collected_in === 'cash_balance' ? 'Cash' :
                        record.collected_in === 'bank_balance' ? 'Bank' :
                        record.collected_in === 'gala_balance' ? 'Gala' : '-';

                      return (
                        <tr key={record.id}>
                          <td>{new Date(record.collected_at).toLocaleString()}</td>
                          <td>
                            <span style={{
                              borderRadius: '4px',
                              fontSize: '8px',
                              fontWeight: '600',
                              color: isGiven ? '#856404' : '#155724',
                              border: isGiven ? '1px solid #ffc107' : '1px solid #4CAF50'
                            }}>
                              {isGiven ? 'Credit Given' : 'Credit Collected'}
                            </span>
                          </td>
                          <td style={{ color: isGiven ? '#dc3545' : '#4CAF50', fontWeight: '700' }}>
                            {isGiven ? '+' : '-'}₹{parseFloat(record.amount_collected).toFixed(2)}
                          </td>
                          <td>
                            {!isGiven && record.collected_in ? (
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.875rem',
                                backgroundColor: record.collected_in === 'cash_balance' ? '#fff3cd' :
                                  record.collected_in === 'bank_balance' ? '#d1ecf1' :
                                  record.collected_in === 'gala_balance' ? '#d4edda' : '#f8f9fa',
                                color: '#000',
                                fontWeight: '600'
                              }}>
                                {collectedInDisplay}
                              </span>
                            ) : '-'}
                          </td>
                          <td>₹{parseFloat(record.previous_outstanding).toFixed(2)}</td>
                          <td>₹{parseFloat(record.new_outstanding).toFixed(2)}</td>
                          <td>{record.collected_by_name}</td>
                          <td>
                            {transactionType === 'collected' && (
                              <button
                                onClick={() => handleDeleteCollection(record)}
                                className="btn btn-danger btn-sm"
                                style={{
                                  padding: '0.25rem 0.75rem',
                                  fontSize: '0.875rem',
                                  borderRadius: '4px'
                                }}
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {creditHolderHistory.length > 0 && (
              <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#fff3cd',
                  borderRadius: '8px',
                  border: '1px solid #ffc107'
                }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#856404' }}>Total Credit Given</p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.5rem', fontWeight: '700', color: '#856404' }}>
                    ₹{creditHolderHistory
                      .filter(r => (r.transaction_type || 'collected') === 'given')
                      .reduce((sum, record) => sum + parseFloat(record.amount_collected), 0)
                      .toFixed(2)}
                  </p>
                </div>
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#e8f5e9',
                  borderRadius: '8px',
                  border: '1px solid #4CAF50'
                }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#2e7d32' }}>Total Credit Collected</p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.5rem', fontWeight: '700', color: '#1b5e20' }}>
                    ₹{creditHolderHistory
                      .filter(r => (r.transaction_type || 'collected') === 'collected')
                      .reduce((sum, record) => sum + parseFloat(record.amount_collected), 0)
                      .toFixed(2)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteConfirm && deleteTarget !== null}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteCollection}
        isDeleting={isDeleting}
        title="Delete Credit Collection"
        message="Are you sure you want to delete this credit collection? This will reverse the transaction and update all related balances."
        details={deleteTarget ? [
          { label: 'Amount Collected', value: `₹${parseFloat(deleteTarget.amount_collected).toFixed(2)}`, highlight: true },
          { label: 'Collected In', value: deleteTarget.collected_in === 'cash_balance' ? 'Cash Balance' : deleteTarget.collected_in === 'bank_balance' ? 'Bank Balance' : 'Gala Balance' },
          { label: 'Collection Date', value: new Date(deleteTarget.collected_at).toLocaleString() },
          { label: 'Collected By', value: deleteTarget.collected_by_name || 'Unknown' }
        ] : []}
        warningMessage={`Deleting this collection will deduct ₹${deleteTarget ? parseFloat(deleteTarget.amount_collected).toFixed(2) : '0.00'} from your organization balance and restore the credit holder's outstanding amount.`}
        confirmButtonText="Delete Collection"
        type="danger"
      />
    </>
  );
};

export default CreditHoldersTab;
